// The dish window (Menu Items screen): its sections, and what each one's status mark says.
// Kitchen, Tax and Sold in are checked against every outlet that sells the dish (the user's outlets),
// by the same rules as the Setup Checklist's D4 and D6b, so the window and the checklist agree.

import type { DineItemOutlet } from 'api/types';

export type DishSection =
  | 'details' | 'kitchen' | 'tax' | 'variants' | 'addons' | 'attributes' | 'photos' | 'pairs' | 'soldin';

/** Sections that need the dish saved first: everything but Details, Kitchen and Tax. */
export const needsSavedDish = (s: DishSection) => !['details', 'kitchen', 'tax'].includes(s);

/** Outlets that list the dish but may not send it to its kitchen station (checklist D4). */
export const unroutedAt = (outlets: DineItemOutlet[]) =>
  outlets.filter((o) => o.isActive && o.isTicketable && !o.stationAllowed);

/** Outlets that list the dish, have tax on and no tax profile in force: their bills are refused (D6b). */
export const untaxedAt = (outlets: DineItemOutlet[], outsideGst: boolean) =>
  outsideGst ? [] : outlets.filter((o) => o.isActive && o.taxOn && !o.hasTaxProfile);

/** Outlets where the dish is on the card. */
export const activeAt = (outlets: DineItemOutlet[]) => outlets.filter((o) => o.isActive);

type Mark = 'ok' | 'warn' | undefined;

/** null outlets = not loaded yet (or a new dish): no mark rather than a wrong one. */
export const kitchenMark = (outlets: DineItemOutlet[] | null): Mark =>
  !outlets || activeAt(outlets).length === 0 ? undefined : unroutedAt(outlets).length > 0 ? 'warn' : 'ok';

export const taxMark = (outlets: DineItemOutlet[] | null, outsideGst: boolean): Mark =>
  outsideGst ? 'ok' : !outlets || activeAt(outlets).length === 0 ? undefined : untaxedAt(outlets, false).length > 0 ? 'warn' : 'ok';

export const soldInMark = (outlets: DineItemOutlet[] | null): Mark =>
  !outlets ? undefined : activeAt(outlets).length === 0 ? 'warn' : 'ok';

/** "Juice Bar, Main" — the outlets' names, for a blocker or a note. */
export const names = (outlets: DineItemOutlet[]) => outlets.map((o) => o.outletName).join(', ');
